import React, { useEffect, useRef, useState } from 'react'
import { styled, Box, IconButton, useMediaQuery } from '@mui/material'
import styles from '@/pages/scss/Accessories.module.scss'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.css'
import {
  Typography,
  Button,
  Dropdown,
  SwitchPageLeft,
  SwitchPageRight
} from '@evocolabs/orkaui'
import theme from '@evocolabs/orkaui/lib/theme.cjs'
import ArrowRight24 from '@evocolabs/icons/lib/ArrowRight24'
import { useCookies } from 'react-cookie'
import { useBackend } from '@/src/providers/BackendProvider'
import { skuMap } from '@/src/data/Checkout'

const Carousel = ({ imgs }) => {
  const usePC = useMediaQuery(theme.breakpoints.up('md'))
  const swiperRef = useRef(null)
  const [swiperIdx, setSwiperIdx] = useState(0)

  useEffect(() => {
    if (!swiperRef.current) {
      return
    }
    // console.log(swiperRef.current.realIndex, swiperIdx)
    if (swiperRef.current.realIndex !== swiperIdx) {
      swiperRef.current.slideToLoop(swiperIdx)
    }
  }, [swiperIdx])

  return (
    <Box className={styles['carousel-wrapper']}>
      <Box className={styles['carousel-wrapper-PC']}>
        <Swiper
          spaceBetween={0}
          slidesPerView={1}
          loop
          onSlideChange={(e) => {
            setSwiperIdx(e.realIndex)
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
          }}
          className={styles['carousel-swiper']}
        >
          <Box className={styles['carousel-arrows']}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              width="100%"
              zIndex="100"
              padding="40px"
            >
              <SwitchPageLeft
                id="product-left-button"
                onClick={() =>
                  setSwiperIdx(
                    swiperIdx - 1 < 0 ? imgs.length - 1 : swiperIdx - 1
                  )
                }
              />
              <SwitchPageRight
                id="product-right-button"
                onClick={() =>
                  setSwiperIdx(
                    swiperIdx + 1 > imgs.length - 1 ? 0 : swiperIdx + 1
                  )
                }
              />
            </Box>
          </Box>
          {imgs.map((item, idx) => (
            <SwiperSlide key={idx}>
              <Box className={styles['carousel-slide']}>
                <img
                  src={item}
                  alt={`${idx}`}
                  className={styles['carousel-img']}
                />
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
        {usePC && (
          <Box className={styles['carousel-navWrapper']}>
            <Box className={styles['carousel-navInnerWrapper']}>
              {imgs.map((item, idx) => (
                <div key={idx} onClick={() => setSwiperIdx(idx)}>
                  <img
                    alt={`${idx}`}
                    src={item}
                    className={styles['carousel-navImg']}
                    style={{
                      border: swiperIdx === idx && '2px solid #1a1a1a'
                    }}
                  />
                </div>
              ))}
            </Box>
          </Box>
        )}
      </Box>
      {!usePC && (
        <Box className={styles['carousel-navDotsWrapper']}>
          {imgs.map((item, idx) => (
            <div
              className={[
                styles['carousel-navDot'],
                swiperIdx === idx && styles['carousel-navDot-active']
              ].join(' ')}
              onClick={() => setSwiperIdx(idx)}
              key={idx}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

const Line = styled('div')`
  width: 100%;
  height: 0;
  border: 0.5px solid ${theme.palette.grey[700]};
  margin-top: 11px;
  margin-bottom: 8px;
`

const Information = ({ data, setIdx }) => {
  const [params, setParams] = useState({
    name: data.title,
    size: data.default ? data.carousel[data.default].size : '',
    quantity: 1
  })
  const size = new Array(data.carousel.length)
  const quantity = [1, 2, 3, 4, 5]
  data.carousel.map((item, idx) => {
    size[idx] = item.size
    return null
  })
  const [cookie, setCookie] = useCookies(['orka-cart'])
  const [availability, setAvailability] = useState(true)
  const { checkProductAvailable } = useBackend()

  useEffect(() => {
    ;(async () => {
      const data = {
        product_name: params.size
          ? `${params.name} ${params.size}`.toLowerCase()
          : `${params.name}`.toLowerCase()
      }
      const res = await checkProductAvailable(data)
      setAvailability(res.data.isAvailable && res.data.stock >= params.quantity)
    })()
  }, [params.size, params.quantity])

  const handleChange = (props) => (event) => {
    setParams({
      ...params,
      [props]: event.target.value
    })
    if (props === 'size') {
      setIdx(size.indexOf(event.target.value))
    }
  }

  const handleBuy = () => {
    setCookie('orka-cart', JSON.stringify(params), { path: '/' })
    window.location.href = '/Checkout'
  }

  return (
    <Box className={styles['info-wrapper']}>
      <Typography variant="h2" sx={{ fontSize: '32px', mb: '8px' }}>
        {data.title}
      </Typography>
      <Box>
        <Typography
          variant="h2"
          sx={{ fontSize: '32px', mr: '8px', display: 'inline' }}
        >
          ${data.price}
        </Typography>
        <Typography variant="h4" sx={{ display: 'inline', mr: '8px' }}>
          {data.currency}
        </Typography>
        <Typography variant="subh1" sx={{ display: 'inline' }}>
          {data.unit}
        </Typography>
      </Box>
      <Line />
      {data.features.map((item) => (
        <Typography variant="body1" key={item} sx={{ mb: '8px' }}>
          {item}
        </Typography>
      ))}
      {data.carousel.length !== 1 && (
        <>
          <Typography variant="h4" sx={{ m: '8px 0 16px' }}>
            Size
          </Typography>
          <Dropdown
            value={params.size}
            choice={size}
            controlSx={{ width: '300px' }}
            onChange={handleChange('size')}
          />
        </>
      )}
      <Typography variant="h4" sx={{ m: '16px 0' }}>
        Quantity
      </Typography>
      <Dropdown
        value={params.quantity}
        choice={quantity}
        controlSx={{ width: '96px' }}
        onChange={handleChange('quantity')}
      />
      <Button
        size="large"
        block
        sx={{ mt: '24px' }}
        handleClick={handleBuy}
        disabled={!availability}
      >
        {availability ? 'buy now' : 'out of stock'}
      </Button>
      <Typography
        variant="caption"
        sx={{ mt: '8px', color: theme.palette.grey[600] }}
      >
        Supported payment types
      </Typography>
      <Box className={styles['info-paymentWrapper']}>
        <img
          src={
            'https://d2mhexn86y1lr4.cloudfront.net/website/product/checkout/Visa%20Classic.svg'
          }
        />
        <img
          src={
            'https://d2mhexn86y1lr4.cloudfront.net/website/product/checkout/Mastercard.svg'
          }
        />
        <img
          src={
            'https://d2mhexn86y1lr4.cloudfront.net/website/product/checkout/Amex.svg'
          }
        />
        <img
          src={
            'https://d2mhexn86y1lr4.cloudfront.net/website/product/checkout/Discover.svg'
          }
        />
        <img
          src={
            'https://d2mhexn86y1lr4.cloudfront.net/website/product/checkout/DinersClubInternational.svg'
          }
        />
        <img
          src={
            'https://d2mhexn86y1lr4.cloudfront.net/website/product/checkout/Interac.svg'
          }
        />
        <img
          src={
            'https://d2mhexn86y1lr4.cloudfront.net/website/product/checkout/ApplePay.svg'
          }
        />
        <img
          src={
            'https://d2mhexn86y1lr4.cloudfront.net/website/product/checkout/GooglePay.svg'
          }
        />
        <img
          src={
            'https://www.paypalobjects.com/digitalassets/c/website/marketing/apac/C2/logos-buttons/optimize/Online_Primary_Acceptance_Mark_RGB_V2_medium.jpg'
          }
        />
      </Box>
      <Box className={styles['info-helpWrapper']}>
        <Typography variant="caption">Need some help?</Typography>
        <Button
          variant="textsecondary"
          size="small"
          onClick={() => {
            window.LiveChatWidget && window.LiveChatWidget.call('maximize')
          }}
        >
          Chat with us online
        </Button>
      </Box>
    </Box>
  )
}

const Details = ({ data }) => {
  const [carouselIdx, setCarouselIdx] = useState(
    data.default ? data.default : 0
  )
  return (
    <Box className={styles['intro-wrapper']}>
      <Carousel imgs={data.carousel[carouselIdx].imgs} />
      <Information data={data} setIdx={setCarouselIdx} />
    </Box>
  )
}

export default Details
